// CRT/VHS composite shader: scanlines, chromatic aberration, vignette, film
// grain, optional barrel warp, and a subtle VHS tracking band.
// One full-screen pass; follows the three.js ShaderPass shader-object
// convention ({ uniforms, vertexShader, fragmentShader }).

import * as THREE from 'three';

export const CrtCompositeShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    // Seconds since pipeline start; drives grain + tracking animation.
    time: { value: 0 },
    // Drawing-buffer size in physical pixels (scanline period unit).
    resolution: { value: new THREE.Vector2(1, 1) },
    scanlineIntensity: { value: 0.5 },
    chromaticAberration: { value: 0.3 },
    noiseIntensity: { value: 0.25 },
    vignetteIntensity: { value: 0.35 },
    barrelWarp: { value: 0.15 },
    // 0 or 1; a float so it can ride the same uniform path as the sliders.
    vhsTracking: { value: 0 }
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform vec2 resolution;
    uniform float scanlineIntensity;
    uniform float chromaticAberration;
    uniform float noiseIntensity;
    uniform float vignetteIntensity;
    uniform float barrelWarp;
    uniform float vhsTracking;
    varying vec2 vUv;

    // Cheap deterministic hash for grain / VHS jitter.
    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    // Radial (barrel) distortion; amount stays small so corners keep readable.
    vec2 warp(vec2 uv, float amount) {
      vec2 cc = uv - 0.5;
      return uv + cc * dot(cc, cc) * amount;
    }

    void main() {
      vec2 uv = vUv;

      if (barrelWarp > 0.001) {
        uv = warp(uv, barrelWarp * 0.25);
      }

      float bandY = fract(time * 0.22);
      float band = smoothstep(0.10, 0.0, abs(uv.y - bandY));
      if (vhsTracking > 0.5) {
        // Thin per-row jitter everywhere.
        float row = floor(uv.y * resolution.y * 0.5);
        uv.x += (hash(vec2(row, floor(time * 24.0))) - 0.5) * 0.0016;
        // Horizontal tear inside the slowly sweeping tracking band; amplitude
        // and frequency kept low so the playfield border wobbles instead of
        // scalloping.
        uv.x += band * 0.006 * sin(uv.y * 80.0 + time * 12.0);
      }

      // Outside the original frame (only possible with barrel warp) sample
      // black so the warp reads as a curved screen edge, not smeared pixels.
      float inFrame = step(0.0, uv.x) * step(uv.x, 1.0)
                    * step(0.0, uv.y) * step(uv.y, 1.0);

      // Chromatic aberration, stronger toward the corners.
      vec2 abOffset = (uv - 0.5) * (chromaticAberration * 0.006);
      vec3 col;
      col.r = texture2D(tDiffuse, uv + abOffset).r;
      col.g = texture2D(tDiffuse, uv).g;
      col.b = texture2D(tDiffuse, uv - abOffset).b;

      // Scanlines: darken alternate physical pixel rows.
      float scan = 0.5 + 0.5 * sin(uv.y * resolution.y * 3.14159265);
      col *= mix(1.0, 0.55 + 0.45 * scan, scanlineIntensity);

      // Brightness noise riding the tracking band.
      if (vhsTracking > 0.5) {
        float row = floor(uv.y * resolution.y * 0.5);
        col += band * (hash(vec2(row, floor(time * 60.0))) - 0.5) * 0.12;
      }

      // Film grain, animated at display rate.
      float grain = hash(uv * resolution * 0.35
        + vec2(fract(time * 13.7), fract(time * 7.3)) * 91.0) - 0.5;
      col += grain * noiseIntensity * 0.14;

      // Vignette (classic 16x weighting, 1.0 at center -> 0.0 at edges).
      float vig = 16.0 * uv.x * uv.y * (1.0 - uv.x) * (1.0 - uv.y);
      col *= mix(1.0, pow(max(vig, 0.0), 0.22), vignetteIntensity);

      gl_FragColor = vec4(col * inFrame, 1.0);
    }
  `
};
